import { ReactNode, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  canManageGroups,
  canRecordContributions,
  isSuperAdmin,
} from '../utils/roles';

export type RouteAccess =
  | 'public'
  | 'authenticated'
  | 'admin'
  | 'billing'
  | 'superAdmin'
  | 'canRecord'
  | 'canManageGroups';

interface ProtectedRouteProps {
  children: ReactNode;
  access?: RouteAccess;
  redirectTo?: string;
}

const DEFAULT_REDIRECTS: Record<Exclude<RouteAccess, 'public'>, string> = {
  authenticated: '/login',
  admin: '/member-dashboard',
  billing: '/member-dashboard',
  superAdmin: '/admin-dashboard',
  canRecord: '/member-dashboard',
  canManageGroups: '/member-dashboard',
};

function hasGroupStaffRole(
  groupRoles?: Array<'admin' | 'clerk' | 'member'>
): boolean {
  return !!groupRoles?.includes('admin') || !!groupRoles?.includes('clerk');
}

function evaluateAccess(
  access: RouteAccess,
  user: { id: string } | null,
  userProfile: {
    role?: string;
    group_roles?: Array<'admin' | 'clerk' | 'member'>;
  } | null,
  effectiveRole: 'admin' | 'member'
): boolean {
  if (access === 'public') return true;
  if (!user || !userProfile) return false;

  const role = userProfile.role;
  const groupRoles = userProfile.group_roles;

  switch (access) {
    case 'authenticated':
      return true;
    case 'admin':
      return effectiveRole === 'admin';
    case 'billing':
      return (
        isSuperAdmin(role) ||
        role === 'group_admin' ||
        role === 'group_creator'
      );
    case 'superAdmin':
      return isSuperAdmin(role);
    case 'canRecord':
      return (
        canRecordContributions(role) || hasGroupStaffRole(groupRoles)
      );
    case 'canManageGroups':
      return canManageGroups(role) || hasGroupStaffRole(groupRoles);
    default:
      return false;
  }
}

const ProtectedRoute = ({
  children,
  access = 'authenticated',
  redirectTo,
}: ProtectedRouteProps) => {
  const { user, userProfile, effectiveRole, loading } = useAuth();
  const navigate = useNavigate();

  const isAllowed = useMemo(
    () => evaluateAccess(access, user, userProfile, effectiveRole),
    [access, user, userProfile, effectiveRole]
  );

  const destination =
    redirectTo ??
    (access === 'public' ? undefined : DEFAULT_REDIRECTS[access]);

  useEffect(() => {
    if (loading || access === 'public') return;
    if (!isAllowed && destination) {
      navigate(destination, { replace: true });
    }
  }, [loading, access, isAllowed, destination, navigate]);

  if (access === 'public') {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAllowed) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
